import { api } from '../client';
import type { PageParams } from '@/types/api';
import type {
  AcademicHistory,
  AccountLink,
  Enrollment,
  IsoDate,
  PromotionResult,
  Student,
  StudentStatusHistory,
  UUID,
} from '@/types/domain';

export interface StudentPayload {
  /** Leave blank to have the server issue STU-YYYY-#####. */
  studentNumber?: string;
  firstName: string;
  middleName?: string;
  lastName: string;
  dateOfBirth: string;
  gender: Student['gender'];
  email?: string;
  phone?: string;
  bloodGroup?: string;
  nationality?: string;
  admissionDate: string;
  /**
   * Create accepts ACTIVE or INACTIVE only; an update refuses any change.
   * Every other move goes through the lifecycle endpoints below.
   */
  status?: Student['status'];
}

/**
 * An enrolment names one grade offering, not a year, campus and grade
 * separately: the offering already is that combination, so the three cannot
 * disagree.
 */
export interface EnrollmentPayload {
  studentId: UUID;
  gradeOfferingId: UUID;
  sectionId?: UUID;
  rollNumber?: string;
  enrollmentDate: string;
  remarks?: string;
}

export interface TransferPayload {
  /** Supply to move to another campus or grade; omit for a section move. */
  targetGradeOfferingId?: UUID;
  targetSectionId: UUID;
  rollNumber?: string;
  effectiveDate: string;
  reason?: string;
}

export interface PromotionPayload {
  targetGradeOfferingId: UUID;
  targetSectionId?: UUID;
  rollNumber?: string;
  effectiveDate: string;
  remarks?: string;
}

export interface BulkPromotionPayload {
  enrollmentIds: UUID[];
  targetGradeOfferingId: UUID;
  targetSectionId?: UUID;
  effectiveDate: string;
  /** When true, reports what would happen without writing anything. */
  preview: boolean;
}

export interface StudentFilters extends PageParams {
  status?: Student['status'];
  gradeOfferingId?: UUID;
  sectionId?: UUID;
}

export const studentsApi = {
  list: (params: StudentFilters) => api.getPage<Student>('/students', { params }),
  get: (id: UUID) => api.get<Student>(`/students/${id}`),
  create: (payload: StudentPayload) => api.post<Student>('/students', payload),
  update: (id: UUID, payload: StudentPayload) => api.put<Student>(`/students/${id}`, payload),
  remove: (id: UUID) => api.delete(`/students/${id}`),
  /** Full placement history, most recent academic year first. */
  enrollments: (id: UUID) => api.get<Enrollment[]>(`/students/${id}/enrollments`),
  /**
   * Every academic year, and every section held within each year.
   *
   * A year in which the student changed section shows both, with the dates each
   * was held — placements are closed rather than overwritten.
   */
  academicHistory: (id: UUID) => api.get<AcademicHistory>(`/students/${id}/academic-history`),

  /*
   * Lifecycle. Each is a named transition the server validates against the
   * current status; a refusal comes back as a message to show verbatim.
   */
  /** Reason required. */
  suspend: (id: UUID, reason: string) =>
    api.post<void>(`/students/${id}/suspend`, undefined, { params: { reason } }),
  reinstate: (id: UUID, reason?: string) =>
    api.post<void>(`/students/${id}/reinstate`, undefined, { params: { reason } }),
  /** Reason required. Also closes the live enrolment. */
  withdraw: (id: UUID, reason: string) =>
    api.post<void>(`/students/${id}/withdraw`, undefined, { params: { reason } }),
  graduate: (id: UUID) => api.post<void>(`/students/${id}/graduate`),
  /** Reason required. */
  readmit: (id: UUID, reason: string) =>
    api.post<void>(`/students/${id}/readmit`, undefined, { params: { reason } }),
  statusHistory: (id: UUID) => api.get<StudentStatusHistory[]>(`/students/${id}/status-history`),

  /** The portal account behind this student; `userId` is absent when none is linked. */
  account: (id: UUID) => api.get<AccountLink>(`/students/${id}/account`),
  linkAccount: (id: UUID, userId: UUID) =>
    api.put<AccountLink>(`/students/${id}/account`, { userId }),
  unlinkAccount: (id: UUID) => api.delete<AccountLink>(`/students/${id}/account`),
};

export const enrollmentsApi = {
  list: (
    params: PageParams & {
      gradeOfferingId?: UUID;
      sectionId?: UUID;
      status?: Enrollment['status'];
    },
  ) =>
    api.getPage<Enrollment>('/student-enrollments', { params }),
  get: (id: UUID) => api.get<Enrollment>(`/student-enrollments/${id}`),
  create: (payload: EnrollmentPayload) => api.post<Enrollment>('/student-enrollments', payload),
  /**
   * Moves a student to another section, or to another campus or grade in the
   * same year.
   *
   * Replaces the old placement update, which overwrote the section in place. The
   * previous placement is now closed the day before the new one begins, so the
   * section a student held on any past date is still answerable. Also used to
   * assign a section for the first time.
   */
  transfer: (id: UUID, payload: TransferPayload) =>
    api.post<Enrollment>(`/student-enrollments/${id}/transfer`, payload),
  /** Closes this enrolment and creates a new one pointing back at it. */
  promote: (id: UUID, payload: PromotionPayload) =>
    api.post<Enrollment>(`/student-enrollments/${id}/promote`, payload),
  /** Promotes a group, reporting an outcome per student rather than failing the batch. */
  promoteAll: (payload: BulkPromotionPayload) =>
    api.post<PromotionResult>('/student-enrollments/promote', payload),
  close: (id: UUID, payload: { status: Enrollment['status']; completionDate: string; remarks?: string }) =>
    api.put<Enrollment>(`/student-enrollments/${id}/close`, payload),
  /** Closes every live enrolment in the year; reports how many it closed. */
  closeYear: (academicYearId: UUID, completionDate: IsoDate) =>
    api.post<{ closed: number }>('/student-enrollments/close-year', undefined, {
      params: { academicYearId, completionDate },
    }),
};
